import { MARK_MUTED_COLOR, METRIC_COLOR } from '../../components/metricColors';
import { kAxis } from '../../components/axisFormat';

/**
 * 訂房曲線 (Pickup) 的 echarts option。
 *
 * 純函式：吃後端 pace-curve 的 items，回一份 option，不碰 state ——
 * 這張圖踩過兩個「畫面看起來有東西、實際是錯的」的坑 (見下方註解)，
 * 而那類錯誤只有把它變成可單測的純函式才抓得到。
 */

const leadLabel = (lead) => (lead === 0 ? '入住日' : `前 ${lead} 天`);
const money = (v) => (v == null ? '—' : 'NT$' + Number(v).toLocaleString());

/** 圖上所有系列的名稱。ADR 兩條預設關閉 —— 六條線同時開會看不出主線。 */
export const SERIES_NAMES = ['今年 (到今天)', '去年', 'RevPAR 今年', 'RevPAR 去年', 'ADR 今年', 'ADR 去年'];

export function buildCurveOption(curveData) {
  const items = (curveData || []).slice().sort((a, b) => b.lead - a.lead); // 90→0
  if (!items.length) return null;
  const x = items.map((it) => leadLabel(it.lead));
  const nowIdx = items.reduce((acc, it, i) => (it.thisYear != null ? i : acc), -1); // 今年線端點 = 現在
  // x 軸用連續的數值軸：提前天數的取樣本來就疏密不均 (90→75 差 15 天、3→0 差 3 天)，
  // 等距分類軸會把兩者畫成一樣寬，訂房曲線「越接近入住越陡」的形狀就被抹平了。
  // 資料點因此要給 [提前天數, 值] 的座標對，位置才會正比於實際天數。
  const pt = (get) => items.map((it) => [it.lead, get(it)]);
  // 正常帶的多邊形頂點：[提前天數, 下界, 上界]，兩端都有值才畫
  const bandPts = items.filter((it) => it.lo != null && it.hi != null).map((it) => [it.lead, it.lo, it.hi]);

  return {
    // 每點 tooltip 同列顯示 在手/ADR/RevPAR/營收 (今年 vs 去年)，看價格調控是否推升 RevPAR/營收；
    // appendToBody+confine 避免被抽屜裁切
    tooltip: { trigger: 'axis', appendToBody: true, confine: true, formatter: (ps) => {
      const i = ps[0].dataIndex, it = items[i]; const rows = [x[i]];
      if (it.thisYear != null) rows.push(`今年　在手 ${it.thisYear}%｜ADR ${money(it.thisYearAdr)}｜RevPAR ${money(it.thisYearRevpar)}｜營收 ${money(it.thisYearRev)}`);
      if (it.ly != null) rows.push(`去年　在手 ${it.ly}%｜ADR ${money(it.lyAdr)}｜RevPAR ${money(it.lyRevpar)}｜營收 ${money(it.lyRev)}`);
      if (it.mid != null) rows.push(`近年中位　在手 ${it.mid}%`);
      return rows.join('<br/>');
    } },
    // 圖例用 plain 讓它自己折行。type:"scroll" 在抽屜寬度下會把六個項目收成可捲動的一排，
    // 捲出去的系列名看不到，使用者只會覺得「圖上那條紫線沒有說明」
    legend: { top: 0, itemWidth: 12, itemHeight: 8, itemGap: 12, textStyle: { fontSize: 11 },
      data: SERIES_NAMES, selected: { 'ADR 今年': false, 'ADR 去年': false } },
    // 上緣留給兩行圖例；左右各留足軸標籤 —— 右軸的 RevPAR 是 "12k" 這種寬字串，
    // 而 x 軸兩端的「前 90 天」「入住日」原本會被切掉半個字
    grid: { top: 52, left: 46, right: 58, bottom: 40 },
    xAxis: { type: 'value', inverse: true, min: 0, max: items[0].lead,
      axisLabel: { fontSize: 10, hideOverlap: true, showMinLabel: true, showMaxLabel: true, formatter: (v) => leadLabel(v) },
      splitLine: { show: false } },
    yAxis: [
      { type: 'value', min: 0, max: 100, axisLabel: { formatter: '{value}%' } },
      { type: 'value', min: 0, position: 'right', axisLabel: { fontSize: 9, formatter: kAxis }, splitLine: { show: false } },
    ],
    series: [
      // 正常帶用 custom series 逐段畫梯形，不用 stack ——
      // 堆疊在數值軸上不成立 (實測資料正確但填色位置整個錯位)，只有分類軸才靠得住。
      // 這裡直接用 api.coord() 把 (提前天數, 上界/下界) 換算成像素再連成多邊形，與軸型別無關。
      { name: '近年正常帶', type: 'custom', silent: true, z: 1, data: bandPts,
        renderItem: (params, api) => {
          const i = params.dataIndex;
          if (i >= bandPts.length - 1) return null;   // 最後一點沒有下一段可連
          const a = bandPts[i], b = bandPts[i + 1];
          return { type: 'polygon',
            shape: { points: [api.coord([a[0], a[2]]), api.coord([b[0], b[2]]),
                              api.coord([b[0], b[1]]), api.coord([a[0], a[1]])] },
            style: { fill: 'rgba(24,144,255,0.14)' } };
        } },
      { name: '近年中位', type: 'line', smooth: true, symbol: 'none', data: pt((it) => it.mid), lineStyle: { type: 'dashed', color: MARK_MUTED_COLOR, width: 1.5 }, itemStyle: { color: MARK_MUTED_COLOR }, z: 2 },
      { name: '去年', type: 'line', smooth: true, symbol: 'circle', symbolSize: 4, data: pt((it) => it.ly), lineStyle: { color: METRIC_COLOR.occ, width: 1.5, type: 'dashed', opacity: 0.7 }, itemStyle: { color: METRIC_COLOR.occ }, z: 3 },
      { name: '今年 (到今天)', type: 'line', smooth: true, symbol: 'circle', symbolSize: 6, connectNulls: false, data: pt((it) => it.thisYear), lineStyle: { color: '#1890ff', width: 2.5 }, itemStyle: { color: '#1890ff' }, z: 4,
        markLine: nowIdx >= 0 ? { silent: true, symbol: 'none', data: [{ xAxis: items[nowIdx].lead, label: { formatter: '現在', fontSize: 10, color: '#fa541c' }, lineStyle: { color: '#fa541c', type: 'dashed', width: 1 } }] } : undefined },
      { name: 'RevPAR 今年', type: 'line', yAxisIndex: 1, smooth: true, symbol: 'none', connectNulls: false, data: pt((it) => it.thisYearRevpar), lineStyle: { color: METRIC_COLOR.revpar, width: 2 }, itemStyle: { color: METRIC_COLOR.revpar }, z: 5 },
      { name: 'RevPAR 去年', type: 'line', yAxisIndex: 1, smooth: true, symbol: 'none', data: pt((it) => it.lyRevpar), lineStyle: { color: METRIC_COLOR.revpar, width: 1.5, type: 'dashed', opacity: 0.75 }, itemStyle: { color: METRIC_COLOR.revpar }, z: 3 },
      { name: 'ADR 今年', type: 'line', yAxisIndex: 1, smooth: true, symbol: 'none', connectNulls: false, data: pt((it) => it.thisYearAdr), lineStyle: { color: METRIC_COLOR.adr, width: 2 }, itemStyle: { color: METRIC_COLOR.adr }, z: 4 },
      { name: 'ADR 去年', type: 'line', yAxisIndex: 1, smooth: true, symbol: 'none', data: pt((it) => it.lyAdr), lineStyle: { color: METRIC_COLOR.adr, width: 1.5, type: 'dashed', opacity: 0.75 }, itemStyle: { color: METRIC_COLOR.adr }, z: 3 },
    ],
  };
}
